// StatusComponent.tsx - FIXED VERSION
import React, { useEffect, useState } from "react";
import styles from "./StatusComponent.module.scss";
import { useDispatch, useSelector } from "react-redux";
import Button from "../UI/Buttons/Button/Button";
import { RootState } from "../../store";
import {
  logoutAction,
  setCNCActiveStatus,
  fetchMarketplaceInfoDetailed,
} from "../../store/auth-actions";
import { authActions } from "../../store/auth-slice";
import axios, { baseUrl } from "../../axios/axios";
import StoreInfo from "./PharmacyDetails/PharmacyDetails";
import PromoCategories from "./PromoCategories/PromoCategories";
import PharmacyImagesAndStatus from "./PharmacyImagesAndStatus/PharmacyImagesAndStatus";
import { useGlobalContext } from "../../store/context/GlobalContext";

type Props = {
  isAdmin?: boolean;
  isPharmacist?: boolean;
  isDriver?: boolean;
  isMarketplaceAdmin?: boolean;
};

const StatusComponent = ({
  isAdmin,
  isPharmacist,
  isDriver,
  isMarketplaceAdmin,
}: Props) => {
  const dispatch = useDispatch();
  const { clearContext } = useGlobalContext();

  // Use Redux state instead of localStorage
  const marketplaceInfo = useSelector(
    (state: RootState) => state.auth.marketplaceInfo,
  );

  const [activeButtonTitle, setActiveButtonTitle] = useState("Status: aktivna");
  const [marketplaceInfoDetailed, setMarketplaceInfoDetailed] = useState<any>(
    {},
  );
  const [marketplaceWorkingHours, setMarketplaceWorkingHours] = useState<any>(
    {},
  );

  // Only fetch when marketplace info is available from Redux
  useEffect(() => {
    if (
      marketplaceInfo?.branchMarketplaceId &&
      marketplaceInfo?.marketplaceId
    ) {
      // DUAL MODE: Fetch for both local state AND Redux
      // Local state - for backward compatibility with existing components
      fetchMarketPlaceDetails(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId,
      ).then((res) => setMarketplaceInfoDetailed(res.data));

      // Redux state - for new PharmacistInfo component in header
      dispatch<any>(
        fetchMarketplaceInfoDetailed(
          marketplaceInfo.branchMarketplaceId,
          marketplaceInfo.marketplaceId,
        ),
      );

      fetchMarketPlaceWorkingHours(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId,
      ).then((res) => setMarketplaceWorkingHours(res.data));
    }
  }, [marketplaceInfo, dispatch]); // ✅ Depend on Redux state, not localStorage

  useEffect(() => {
    if (marketplaceInfo && marketplaceInfo?.takesOrders === 1) {
      setActiveButtonTitle("Status: aktivna");
    } else {
      setActiveButtonTitle("Status: neaktivna");
    }
  }, [marketplaceInfo]);

  const logout = () => {
    clearContext(); // Clear global state
    dispatch<any>(
      logoutAction(
        marketplaceInfo?.branchMarketplaceId ?? null,
        marketplaceInfo?.marketplaceId ?? null,
        0,
      ),
    );
  };

  const logoutAdmin = () => {
    clearContext(); // Clear global state
    dispatch(authActions.logout());
  };

  const statusToggle = () => {
    // Add guard for marketplace info
    if (
      !marketplaceInfo?.branchMarketplaceId ||
      !marketplaceInfo?.marketplaceId
    ) {
      console.warn("Cannot toggle status: marketplace info not available");
      return;
    }

    dispatch<any>(
      setCNCActiveStatus(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId,
        marketplaceInfo.takesOrders === 1 ? 0 : 1,
      ),
    );
  };

  // Add loading state while marketplace info loads
  if (
    !marketplaceInfo &&
    !isAdmin &&
    !isPharmacist &&
    !isDriver &&
    !isMarketplaceAdmin
  ) {
    return (
      <div className={styles.StatusComponent}>
        <div className={styles.Content}>
          <span>Loading marketplace info...</span>
        </div>
      </div>
    );
  }

  // Rest of component remains the same...
  if (!isAdmin && !isPharmacist && !isDriver && !isMarketplaceAdmin) {
    return (
      <div className={styles.StatusComponent}>
        <div className={styles.statusComponentStore}>
          <div className={styles.columns}>
            <PharmacyImagesAndStatus
              marketplaceInfo={marketplaceInfo}
              marketplaceInfoDetailed={marketplaceInfoDetailed}
              statusToggle={statusToggle}
              activeButtonTitle={activeButtonTitle}
              logout={logout}
              setMarketplaceInfoDetailed={setMarketplaceInfoDetailed}
            />
          </div>
          <div className={styles.columns}>
            <StoreInfo
              marketplaceInfoDetailed={marketplaceInfoDetailed}
              marketplaceWorkingHours={marketplaceWorkingHours}
            />
          </div>
          <div className={styles.columns}>
            <PromoCategories />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.StatusComponent}>
      {!isAdmin && !isPharmacist && !isDriver && !isMarketplaceAdmin ? (
        <div className={styles.Content}>
          <span className={styles.Title}>Status apoteke</span>
          <span className={styles.PharmacyName}>
            {marketplaceInfo && marketplaceInfo.name}
          </span>
          <div className={styles.Buttons}>
            <Button
              buttonType={marketplaceInfo?.takesOrders === 1 ? "blue" : "red"}
              onClick={statusToggle}
            >
              {activeButtonTitle}
            </Button>
            <Button buttonType="blue" onClick={logout}>
              Odjavi se
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.Content}>
          {isAdmin || isDriver ? (
            <span className={styles.Title}>Admin</span>
          ) : isMarketplaceAdmin ? (
            <span className={styles.Title}>Administrator ustanove</span>
          ) : (
            <span className={styles.Title}>Farmaceut</span>
          )}
          <div className={styles.Buttons}>
            <Button buttonType="blue" onClick={logoutAdmin}>
              Odjavi se
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusComponent;

// Add marketplace info guards to utility functions
export const fetchMarketPlaceDetails = (branchId: any, marketplaceId: any) => {
  if (!branchId || !marketplaceId) {
    console.warn("Cannot fetch marketplace details: missing IDs");
    return Promise.reject("Missing marketplace IDs");
  }
  return axios.get(baseUrl + `/marketplace/${marketplaceId}/${branchId}`, {});
};

export const fetchMarketPlaceWorkingHours = (
  branchId: any,
  marketplaceId: any,
) => {
  if (!branchId || !marketplaceId) {
    console.warn("Cannot fetch working hours: missing IDs");
    return Promise.reject("Missing marketplace IDs");
  }
  return axios.get(
    baseUrl + `/marketplace/opening_hours/${marketplaceId}/${branchId}`,
    {},
  );
};
